import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MapPinIcon, PhoneIcon, XIcon } from 'lucide-react';
import { business, navigation, routes } from '../data/site';
import { SiteLink } from './SiteLink';
type MobileMenuProps = {
  open: boolean;
  onClose: () => void;
};
export function MobileMenu({
  open,
  onClose
}: MobileMenuProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    closeRef.current?.focus();
    const {
      overflow
    } = document.body.style;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);
  if (!open) return null;
  return <div className="fixed inset-0 z-50 xl:hidden" data-id="element-254" data-mp="1yelojcdmoh9j">
      <div className="absolute inset-0 bg-ink/60" onClick={onClose} aria-hidden="true" data-id="element-255" data-mp="2uzch75c6agqm" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Site menu" className="absolute inset-y-0 right-0 flex w-full max-w-[360px] flex-col overflow-y-auto bg-white" data-id="element-256" data-mp="2q7camu1lkpl2">
        <div className="flex items-center justify-between border-b border-ink/10 px-5 py-3" data-id="element-257" data-mp="t8ltsvuwm7vo">
          <span className="text-[15px] font-bold uppercase text-ink" data-id="element-258" data-mp="ejpnb38hx2x6">Menu</span>
          <button ref={closeRef} type="button" onClick={onClose} className="flex h-11 items-center gap-1.5 rounded-btn px-3 text-[15px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-259" data-mp="jiabu5edxy5s">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-260" data-mp="3rr0tvb3baukf" />
            Close
          </button>
        </div>

        <nav aria-label="Mobile" className="px-5 py-2" onClick={event => {
        if ((event.target as HTMLElement).closest('a')) onClose();
      }} data-id="element-261" data-mp="jy2aijkam9o">
          <ul className="divide-y divide-ink/10" data-id="element-262" data-mp="1uz0kdv6a722k">
            {navigation.map(item => {
            const isExpanded = expanded === item.label;
            const panelId = `mobile-panel-${item.label.replace(/\W+/g, '-')}`;
            return <li key={item.label} className="py-1" data-id="element-263" data-mp="3afdh6ko2xh73">
                  <div className="flex items-center justify-between gap-2" data-id="element-264" data-mp="3p7e88l89fuif">
                    <SiteLink to={item.href} className="flex min-h-[48px] flex-1 items-center text-[17px] font-medium text-ink" data-id="element-265" data-mp="3kf4awk4tfs64">
                      {item.label}
                    </SiteLink>
                    {item.children && <button type="button" aria-expanded={isExpanded} aria-controls={panelId} aria-label={`${isExpanded ? 'Hide' : 'Show'} ${item.label} links`} onClick={() => setExpanded(isExpanded ? null : item.label)} className="flex h-11 w-11 items-center justify-center rounded-btn text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-266" data-mp="c0cipbgigzoq">
                        <ChevronDownIcon className={`h-5 w-5 transition-transform duration-150 ease-out ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-267" data-mp="2f7bkfjd0oiz1" />
                      </button>}
                  </div>
                  {item.children && <ul id={panelId} hidden={!isExpanded} className="pb-2 pl-3" data-id="element-268" data-mp="1win2955x4rqy">
                      {item.children.map(child => <li key={child.label} data-id="element-269" data-mp="21f3ofttk4w1e">
                          <SiteLink to={child.href} className="flex min-h-[44px] items-center text-[16px] text-ink/80" data-id="element-270" data-mp="268q2nqfdd7zb">
                            {child.label}
                          </SiteLink>
                        </li>)}
                    </ul>}
                </li>;
          })}
          </ul>
        </nav>

        <div className="mt-auto border-t border-ink/10 px-5 py-4" onClick={event => {
        if ((event.target as HTMLElement).closest('a')) onClose();
      }} data-id="element-271" data-mp="3my7glb76srot">
          <SiteLink to={routes.location} className="flex min-h-[48px] w-full items-center justify-center rounded-btn bg-pegasus-blue px-4 text-[16px] font-bold text-white" data-id="element-272" data-mp="3d48hym6bnq1m">
            Visit Showroom
          </SiteLink>
          <div className="mt-3 flex flex-col gap-2" data-id="element-273" data-mp="2zb91ohyptrt0">
            <a href={business.phoneHref} className="inline-flex min-h-[44px] items-center gap-2 text-[16px] font-medium text-ink" data-id="element-274" data-mp="2ubmna575i5im">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-275" data-mp="2pee1lumsnjcm" />
              Call or text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[44px] items-center gap-2 text-[16px] font-medium text-ink" data-id="element-276" data-mp="2apuj4k0tzo4s">
              <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-277" data-mp="2fmkyt68q4hpo" />
              {business.street}, {business.cityStateZip}
            </SiteLink>
          </div>
        </div>
      </div>
    </div>;
}